/**
 * How heavily a question has been used, and whether that is now a problem.
 *
 * Reuse is not a performance statistic, though the preview used to file it as
 * one. A question that has never been sat has no p-value and no discrimination,
 * so the whole Performance block stands down and says so, and the use count
 * went down with it. That is backwards: how many times an item has been put in
 * front of students is exactly what an author wants to know about an item with
 * no numbers yet, because it is the only history it has.
 *
 * Two ways an item can be overused, and they are different claims:
 *
 * - **Same course.** It was already on a paper these students sat. They have
 *   seen it, so it measures recall of the last exam. This is the product's
 *   existing rule (`usedInSameCourse`) and the reason `QuestionListItem` has
 *   said "Overused" for as long as it has existed.
 * - **Exposure.** It has been used often enough across cohorts that copies are
 *   likely circulating, even where no course link is recorded. Surfaces such as
 *   the bank have no course context at all, so without this they could never
 *   warn about anything.
 */

import { hashUnit } from "@/lib/mock/assessment-add-questions"

/**
 * Uses at which exposure alone is worth a warning.
 *
 * Four cohorts is roughly two years of a twice-yearly paper, which is about how
 * long an item stays private in practice. Below this the count is reported
 * without comment, because reuse is normal and a bank that cried wolf on the
 * second use would be ignored by the third.
 */
export const REUSE_OVERUSED_AT = 4

export type QuestionReuseReason = "same-course" | "exposure"

export interface QuestionReuse {
  /** Times the item has been put in front of students. `0` means never. */
  timesUsed: number
  overused: boolean
  /** Why it is flagged. `null` when it is not. */
  reason: QuestionReuseReason | null
}

export interface QuestionReuseInput {
  /** Seeds the count when the row carries no real one. */
  id: string
  /** The real count, where a surface has one. */
  timesUsed?: number
  /** Already on a paper in the course being built. */
  usedInSameCourse?: boolean
  /**
   * Forces `0` for rows known never to have been used. Without it an item with
   * no recorded count would be given a derived one, and a brand new question
   * would claim a history it does not have.
   */
  neverUsed?: boolean
  /**
   * The item has exam statistics, so a cohort has sat it at least once. Only
   * lifts a *derived* count off zero: a panel that reports 192 examinees under
   * a line saying nobody has sat this is reporting on two different questions.
   */
  sat?: boolean
}

/**
 * Derived when a surface has no real count, so the same question reports the
 * same history everywhere it appears rather than a new number per render.
 */
function derivedTimesUsed(id: string): number {
  return Math.floor(hashUnit(`${id}:reuse`) * 7)
}

export function questionReuse(input: QuestionReuseInput): QuestionReuse {
  const timesUsed = input.neverUsed
    ? 0
    : (input.timesUsed ??
      Math.max(derivedTimesUsed(input.id), input.sat ? 1 : 0))

  // Same course wins when both apply: it is the more specific claim, and it is
  // the one the author can act on by swapping the item out of this paper.
  const reason: QuestionReuseReason | null = input.usedInSameCourse
    ? "same-course"
    : timesUsed >= REUSE_OVERUSED_AT
      ? "exposure"
      : null

  return { timesUsed, overused: reason !== null, reason }
}

/** "Used 3 times", for a line that has room to say it in words. */
export function reuseCountLabel(reuse: QuestionReuse): string {
  if (reuse.timesUsed === 0) return "Never used"
  if (reuse.timesUsed === 1) return "Used once"
  return `Used ${reuse.timesUsed} times`
}

/** The sentence under the count. `null` when there is nothing to warn about. */
export function reuseWarning(reuse: QuestionReuse): string | null {
  switch (reuse.reason) {
    case "same-course":
      return "These students have already sat this question in this course."
    case "exposure":
      return `Used ${reuse.timesUsed} times across cohorts, so copies are likely to be circulating.`
    default:
      return null
  }
}
